import { useState } from 'react';
import { MasterDocument } from '@util/firestore/master';
import { getMasterSpecRows } from '@util/master/extractKeySpecs';

interface SpecificationsSectionProps {
  master: MasterDocument;
  /** Optional heading rendered above the table. Only shown when specs exist,
   *  so callers never end up with an orphan heading over an empty section. */
  heading?: string;
}

const MAX_VISIBLE_ROWS = 12;

export default function SpecificationsSection({ master, heading }: SpecificationsSectionProps) {
  const [expanded, setExpanded] = useState(false);

  // Single source of truth for the rendered rows (extractKeySpecs + the
  // prepended "Use Cases" row) — shared with MasterProductsPageV2's section
  // gate so the tab, heading, and content never disagree.
  const specs = getMasterSpecRows(master);

  if (specs.length === 0) {
    return null;
  }

  const showReadMore = specs.length > MAX_VISIBLE_ROWS;

  return (
    <div>
      {heading && (
        <h3 className="mt-[2rem] mb-[0.8rem] text-[1.5rem] font-semibold">{heading}</h3>
      )}
      <div className="border border-gray-300 rounded-[0.4rem] overflow-hidden">
        <table className="w-full text-[1.5rem] border-collapse" aria-label={`${master.brand} ${master.model} Technical Specifications`}>
          <caption className="sr-only">
            {master.brand} {master.model} full specifications
          </caption>
          <thead>
            <tr className="bg-gray-100 border-b border-gray-300">
              <th className="text-left py-[0.8rem] px-[1.2rem] font-bold text-black w-[40%]">
                Spec
              </th>
              <th className="text-left py-[0.8rem] px-[1.2rem] font-bold text-black">
                Detail
              </th>
            </tr>
          </thead>
          <tbody>
            {specs.map((spec, i) => (
              <tr
                key={i}
                className={`border-b border-gray-200 last:border-b-0${
                  !expanded && i >= MAX_VISIBLE_ROWS ? ' hidden' : ''
                }`}
              >
                <th
                  scope="row"
                  className="text-left py-[0.8rem] px-[1.2rem] font-medium text-brand-dark-gray w-[40%] align-top"
                >
                  {spec.label}
                </th>
                <td className="py-[0.8rem] px-[1.2rem] text-gray-600 align-top">
                  {spec.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showReadMore && (
        <button
          type="button"
          className="text-brand-orange font-medium mt-[0.8rem] cursor-pointer hover:underline text-[1.4rem]"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? 'Show Less' : 'Read More'}
        </button>
      )}
    </div>
  );
}
